import { useState, useRef, useEffect } from 'react';

/**
 * Custom hook for managing batch validation operations with queue support
 * @param {Array} stores - Array of store objects
 * @returns {Object} Batch validation state and control functions
 */
export function useBatchValidation(stores = []) {
    const [batchValidation, setBatchValidation] = useState({
        isRunning: false,
        progress: 0,
        currentStore: null,
        results: [],
        queueLength: 0
    });

    const [validationResults, setValidationResults] = useState([]);
    const validationQueueRef = useRef([]);
    const isProcessingRef = useRef(false);

    /**
     * Process a single batch validation
     * @param {Array} storeIds - Array of store IDs to validate
     */
    const processBatchValidation = async (storeIds) => {
        const results = [];

        for (let i = 0; i < storeIds.length; i++) {
            const storeId = storeIds[i];
            const store = stores.find(s => s._id === storeId);

            setBatchValidation(prev => ({
                ...prev,
                progress: Math.round((i / storeIds.length) * 100),
                currentStore: store?.name || 'Unknown Store'
            }));

            try {
                const response = await fetch(`/api/stores/${encodeURIComponent(store.name)}/validate`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    }
                });

                if (!response.ok) {
                    throw new Error(`Validation failed: ${response.statusText}`);
                }

                const data = await response.json();

                if (data.success) {
                    results.push({
                        storeId,
                        storeName: store.name,
                        validationResult: data.validationResults,
                        timestamp: new Date().toISOString()
                    });
                } else {
                    results.push({
                        storeId,
                        storeName: store.name,
                        error: data.error || 'Validation failed',
                        timestamp: new Date().toISOString()
                    });
                }
            } catch (error) {
                results.push({
                    storeId,
                    storeName: store.name,
                    error: error.message,
                    timestamp: new Date().toISOString()
                });
            }

            // Add delay between requests to avoid overwhelming the server
            if (i < storeIds.length - 1) {
                await new Promise(resolve => setTimeout(resolve, 1000));
            }
        }

        // Update validation results
        const successfulResults = results
            .filter(r => r.validationResult)
            .map(r => ({
                storeId: r.storeId,
                storeName: r.storeName,
                ...r.validationResult
            }));

        setValidationResults(prev => [
            ...prev.filter(v => !successfulResults.some(s => s.storeId === v.storeId)),
            ...successfulResults
        ]);

        return results;
    };

    /**
     * Process the validation queue
     */
    const processQueue = async () => {
        if (isProcessingRef.current || validationQueueRef.current.length === 0) {
            return;
        }

        isProcessingRef.current = true;

        while (validationQueueRef.current.length > 0) {
            const storeIds = validationQueueRef.current.shift();

            setBatchValidation(prev => ({
                ...prev,
                isRunning: true,
                progress: 0,
                currentStore: null,
                results: [],
                queueLength: validationQueueRef.current.length
            }));

            try {
                const results = await processBatchValidation(storeIds);

                setBatchValidation(prev => ({
                    ...prev,
                    progress: 100,
                    results,
                    queueLength: validationQueueRef.current.length
                }));

                // Small delay between batches
                await new Promise(resolve => setTimeout(resolve, 500));
            } catch (error) {
                console.error('Batch validation error:', error);
            }
        }

        setBatchValidation(prev => ({
            ...prev,
            isRunning: false,
            currentStore: null,
            queueLength: 0
        }));

        isProcessingRef.current = false;
    };

    /**
     * Add batch validation to queue
     * @param {Array} storeIds - Array of store IDs to validate
     */
    const runBatchValidation = async (storeIds = []) => {
        if (storeIds.length === 0) {
            alert('Please select stores to validate');
            return;
        }

        // Add to queue
        validationQueueRef.current.push(storeIds);

        setBatchValidation(prev => ({
            ...prev,
            queueLength: validationQueueRef.current.length
        }));

        // Start processing if not already running
        processQueue();
    };

    /**
     * Reset batch validation state and clear queue
     */
    const resetBatchValidation = () => {
        validationQueueRef.current = [];
        isProcessingRef.current = false;
        setBatchValidation({
            isRunning: false,
            progress: 0,
            currentStore: null,
            results: [],
            queueLength: 0
        });
    };

    /**
     * Clear validation results
     */
    const clearValidationResults = () => {
        setValidationResults([]);
    };

    return {
        batchValidation,
        validationResults,
        runBatchValidation,
        resetBatchValidation,
        clearValidationResults,
        setBatchProgress: (progress) => setBatchValidation(prev => ({ ...prev, progress })),
        setCurrentStore: (storeName) => setBatchValidation(prev => ({ ...prev, currentStore: storeName }))
    };
}

export default useBatchValidation;
